import React, { useCallback } from 'react';
import { connect } from 'react-redux';
import cn from 'classnames';

import Button from 'components/form/Button.component';
import Spinner from 'components/generic/Spinner.component';
import Tooltip from 'components/generic/Tooltip.component';
import TooltipContentReceptionist from 'components/generic/TooltipContentReceptionist.component';
import CurrencySign from 'components/price/CurrencySign.component';
import DEPOSIT_SOURCE from 'constants/payment/depositSource.const';
import S from 'constants/string.const';
import useStateWhileMounted from 'hooks/useStateWhileMounted.hook';
import useTip from 'hooks/useTip.hook';
import { useTranslation } from 'providers/GILT.provider';
import { selectPinUserIsCustomerSupport } from 'selectors/simple';
import { isEffectiveReceptionist } from 'selectors/user';
import asArray from 'utils/arrays/asArray.util';
import fromAnyToTrimmed from 'utils/converters/any/fromAnyToTrimmed.util';
import { fromEventToValue } from 'utils/converters/event.util';
import fromAnyToBool from 'utils/converters/fromAnyToBool.util';
import call from 'utils/functions/call.util';
import alwaysNoop from 'utils/functions/nullary/alwaysNoop.util';
import createNodeKey from 'utils/react/createNodeKey.util';

import ClientDepositDetailsRow from 'settings/balance-sheet/ClientDepositDetailsRow.component';

import CN from './ClientDepositDetails.module.scss';

const SPINNER_COLOR = 'white';

const capAmount = (options) => (parseFloat(options?.value) > parseFloat(options?.max) ? options?.max : options?.value);

const RefundCard = ({ clientId, nominal, available, locked, onRefund, tag, label, source, hidden, showTooltip }) => {
  const [amount, setAmount] = useStateWhileMounted('');
  const { t } = useTranslation();

  const { val: isMessageRendered, yay: showMessage, nay: hideMessage } = useTip(false);
  const { val: isInputFocused, yay: focusInput, nay: unfocusInput } = useTip(false);

  const handleChange = useCallback(
    (ev) => {
      const newAmount = capAmount({ value: fromEventToValue(ev), max: available });
      setAmount(newAmount);
      if (newAmount === 0) {
        showMessage();
      } else {
        hideMessage();
      }
    },
    [available, hideMessage, setAmount, showMessage],
  );

  const handleClick = useCallback(
    //
    () => void call('RefundCard.onRefund', onRefund, { amount, source, clientId, nominal, available, locked }),
    [amount, source, clientId, nominal, available, locked, onRefund],
  );

  const handleBlur = useCallback(() => {
    unfocusInput();
    hideMessage();
  }, [unfocusInput, hideMessage]);

  const handleFocus = focusInput;

  if (hidden) return <div />;

  return (
    <div data-bem="RefundCard" data-tag={fromAnyToTrimmed(tag)} className={CN.refundCard}>
      <div data-bem="RefundCard__label" className={CN.depositLabel}>
        {label}
      </div>
      <div data-bem="RefundCard__input-box" className={CN.inputContainer}>
        <CurrencySign />
        {S.space}

        <input
          data-bem="RefundCard__input"
          className={CN.refundInput}
          type="number"
          placeholder=""
          onChange={handleChange}
          onClick={hideMessage}
          onBlur={handleBlur}
          onFocus={handleFocus}
          value={amount}
        />
        {fromAnyToBool(isMessageRendered && isInputFocused) && (
          <div
            className={cn({
              [CN.message]: true,
              [CN.pushRight]: source === DEPOSIT_SOURCE.other || source === DEPOSIT_SOURCE.online,
            })}
          >
            {t('balanceSheet.refundCard.messageText')}
          </div>
        )}
      </div>
      {showTooltip ? (
        <Tooltip variant="top" content={<TooltipContentReceptionist />}>
          <Button tag="RefundCard__button" variant="primary" width="medium" disabled={!available} onClick={alwaysNoop}>
            {t('balanceSheet.refundCard.refundButton')}
          </Button>
        </Tooltip>
      ) : (
        <Button tag="RefundCard__button" variant="primary" width="medium" onClick={handleClick} disabled={!available}>
          {t('balanceSheet.refundCard.refundButton')}
        </Button>
      )}
    </div>
  );
};

const ClientDepositDetails = ({
  appointmentStartTimeList,
  data,
  isCustomerSupport: isCustomerSupportFromProps,
  loading: isLoading,
  onRefund,
  outstanding,
  isReceptionist,
}) => {
  const { t } = useTranslation();
  // just make sure it's a boolean
  const isCustomerSupport = fromAnyToBool(isCustomerSupportFromProps);

  if (isLoading) return <Spinner color={SPINNER_COLOR} />;

  return (
    <div data-bem="ClientDepositDetails" className={CN.component}>
      <div>
        <div className="amount-container">
          <div data-bem="ClientDepositDetails__panel" className="cfix">
            <div
              className={cn({
                [CN.detailsPanel]: true,
                'amount-flex-container': true,
              })}
            >
              <div />

              <RefundCard
                source={DEPOSIT_SOURCE.cash}
                tag="ClientDepositDetails__input-card--cash"
                label={t('balanceSheet.tableHeaders.cash')}
                clientId={outstanding?.clientId}
                nominal={outstanding?.cash}
                available={outstanding?.cash_available}
                locked={outstanding?.cash_locked}
                onRefund={onRefund}
                showTooltip={isReceptionist}
              />

              <RefundCard
                source={DEPOSIT_SOURCE.card}
                tag="ClientDepositDetails__input-card--card-non-slick"
                label={`${t('balanceSheet.tableHeaders.card')} ${t('balanceSheet.tableHeaders.nonSlick')}`}
                clientId={outstanding?.clientId}
                nominal={outstanding?.card}
                available={outstanding?.card_available}
                locked={outstanding?.card_locked}
                onRefund={onRefund}
                showTooltip={isReceptionist}
              />

              <RefundCard
                source={DEPOSIT_SOURCE.pos}
                tag="ClientDepositDetails__input-card--slick-pos"
                label={`${t('balanceSheet.tableHeaders.card')} ${t('balanceSheet.tableHeaders.slickPay')}`}
                clientId={outstanding?.clientId}
                nominal={outstanding?.slick_pos}
                available={outstanding?.slick_pos_available}
                locked={outstanding?.slick_pos_locked}
                onRefund={onRefund}
                showTooltip={isReceptionist}
              />

              <RefundCard
                source={DEPOSIT_SOURCE.online}
                tag="ClientDepositDetails__input-card--online"
                label={t('balanceSheet.tableHeaders.online')}
                clientId={outstanding?.clientId}
                nominal={outstanding?.online}
                available={outstanding?.online_available}
                locked={outstanding?.online_locked}
                onRefund={onRefund}
                showTooltip={isReceptionist}
              />

              <RefundCard
                source={DEPOSIT_SOURCE.other}
                tag="ClientDepositDetails__input-card--other"
                label={t('common.other')}
                clientId={outstanding?.clientId}
                nominal={outstanding?.other}
                available={outstanding?.other_available}
                locked={outstanding?.other_locked}
                onRefund={onRefund}
                showTooltip={isReceptionist}
              />

              <div />
              <div />
            </div>
            <div className="log-title">{t('balanceSheet.depositLogTable.title')}</div>
          </div>

          <table data-bem="ClientDepositDetails__balance" className="balance">
            <thead>
              <tr>
                <th>{t('balanceSheet.depositLogTable.headers.date')}</th>
                <th>{t('balanceSheet.depositLogTable.headers.depositType')}</th>
                <th>{t('balanceSheet.depositLogTable.headers.amount')}</th>
                <th>{t('balanceSheet.depositLogTable.headers.lastAction')}</th>
                <th>{t('balanceSheet.depositLogTable.headers.relates')}</th>
                {isCustomerSupport && <th>{t('balanceSheet.depositLogTable.headers.id')}</th>}
              </tr>
            </thead>
            <tbody>
              {asArray(data?.deposits_list).map((deposit, index) => (
                <ClientDepositDetailsRow
                  key={createNodeKey({ index, tag: deposit.start_time })}
                  times={appointmentStartTimeList}
                  deposit={deposit}
                  showIntentIdColumn={isCustomerSupport}
                />
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  isCustomerSupport: selectPinUserIsCustomerSupport(state),
  isReceptionist: isEffectiveReceptionist(state),
});

const ClientDepositDetailsHoc = connect(mapStateToProps, null)(ClientDepositDetails);

export default ClientDepositDetailsHoc;
